import { useId, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import type { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import { describeFailure } from '@/utils/fulfillmentErrors';
import { describeSampleWindowOutcome, type SampleWindowOutcome } from '@/utils/fulfillmentMarketing';
import { kstDay } from '@/utils/fulfillmentTime';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';
import '../../ultimateDesign.css';

interface SampleAssignmentOnModalProps {
  readonly show: boolean;
  readonly client: FulfillmentAdminClient;
  /** The orders ticked on the list; the selected-orders choice is unreachable while this is empty. */
  readonly selectedOrderIds: number[];
  readonly onHide: () => void;
  readonly onStarted: () => void;
}

/**
 * Today in Seoul, so the period opens on the day the warehouse is living in. The UTC day is still
 * yesterday until 09:00 KST, which prefilled a day the form then refused as already past.
 */
const today = (): string => kstDay(new Date().toISOString());

/** Seoul has no daylight saving, so the offset the operator is typing in is always +09:00. */
const KST_OFFSET = '+09:00';

/** The day and time as the desk means them in Seoul, as the instant the server stores. */
const toTimestamp = (date: string, time: string): string => new Date(`${date}T${time}:00${KST_OFFSET}`).toISOString();

const NO_START = 'The window needs a start.';
const NO_START_TIME = 'Enter a start time.';
const START_IN_PAST = 'The window cannot start in the past.';
const END_BEFORE_START = 'The window must end after it starts.';
const MISSING_END = 'Enter an end date and time, or choose forever.';

/** Why the window cannot be opened, in the operator's words, or null when it can [T18]. */
const windowError = (
  startDate: string,
  startTime: string,
  forever: boolean,
  endDate: string,
  endTime: string,
): string | null => {
  if (!startDate.trim()) {
    return NO_START;
  }
  // A blank time used to open the window at midnight, hours before anyone asked it to.
  if (!startTime.trim()) {
    return NO_START_TIME;
  }
  // Day granularity: a window opened for today at 10:00 is still today's window at 11:00.
  if (startDate < today()) {
    return START_IN_PAST;
  }
  if (forever) {
    return null;
  }
  if (!endDate.trim() || !endTime.trim()) {
    return MISSING_END;
  }
  return toTimestamp(endDate, endTime) <= toTimestamp(startDate, startTime) ? END_BEFORE_START : null;
};

/**
 * A selected-orders window gives the set to orders that already exist the moment it opens, so it has no
 * period to pick. It is sent as starting at the top of today in Seoul: never in the server's future,
 * which it refuses for this target, and never ending, since there is nothing later for it to do.
 */
const appliedNow = (): string => toTimestamp(today(), '00:00');

/** The window carries a period and a target only: the products are the one configured sample set [wireframe M2]. */
export function SampleAssignmentOnModal({
  show,
  client,
  selectedOrderIds,
  onHide,
  onStarted,
}: SampleAssignmentOnModalProps) {
  const [target, setTarget] = useState<'ALL_NEW' | 'SELECTED'>('ALL_NEW');
  const [startDate, setStartDate] = useState(today());
  const [startTime, setStartTime] = useState('10:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [forever, setForever] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** What the server said the window did; shown in place of the form until the desk closes it. */
  const [outcome, setOutcome] = useState<SampleWindowOutcome | null>(null);

  const selected = target === 'SELECTED';
  const invalid = selected ? null : windowError(startDate, startTime, forever, endDate, endTime);
  const windowErrorId = useId();
  const missingEndDate = invalid === MISSING_END && !endDate.trim();
  const missingEndTime = invalid === MISSING_END && !endTime.trim();
  // A window that opens later is not running yet, and the list will show it as SCHEDULED [T18].
  const scheduled = !selected && invalid === null && toTimestamp(startDate, startTime) > new Date().toISOString();

  const close = () => {
    setOutcome(null);
    setError(null);
    onHide();
  };

  const start = async () => {
    if (invalid) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await client.createSampleRule(
        selected
          ? { target, orderIds: selectedOrderIds, activeFrom: appliedNow(), activeTo: null }
          : {
              target,
              activeFrom: toTimestamp(startDate, startTime),
              activeTo: forever ? null : toTimestamp(endDate, endTime),
            },
      );
      setOutcome(describeSampleWindowOutcome(created));
      onStarted();
    } catch (failure) {
      // The server says why it refused — no sample set, an order list it cannot use — and the desk acts on that.
      setError(describeFailure(failure, 'Could not start the assignment. Nothing was changed.'));
    } finally {
      setBusy(false);
    }
  };

  if (outcome) {
    return (
      <Modal show={show} onHide={close} {...ULTIMATE_MODAL}>
        <Modal.Header closeButton>
          <Modal.Title>Sample Assignment ON</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant={outcome.clean ? 'success' : 'warning'} className="mb-0" role="status">
            <div className="fw-semibold">{outcome.summary}</div>
            {outcome.details.length > 0 && (
              <ul className="mb-0 mt-2 small">
                {outcome.details.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={close}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={close} {...ULTIMATE_MODAL}>
      <Modal.Header closeButton>
        <Modal.Title>Sample Assignment ON</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="fw-semibold mb-2">Assignment Target</div>
        <Form.Check
          type="radio"
          name="sampleTarget"
          id="sample-target-all"
          label="All new orders in this period"
          checked={target === 'ALL_NEW'}
          onChange={() => setTarget('ALL_NEW')}
          className="border rounded p-2 mb-2"
        />
        <Form.Check
          type="radio"
          name="sampleTarget"
          id="sample-target-selected"
          label={`Selected orders only (${selectedOrderIds.length})`}
          checked={selected}
          disabled={selectedOrderIds.length === 0}
          onChange={() => setTarget('SELECTED')}
          className="border rounded p-2 mb-3"
        />

        {selected ? (
          <div className="text-muted small">
            The sample set is added to the selected orders as soon as you start. An order that already has a sample set
            keeps the one it has.
          </div>
        ) : (
          <>
            <div className="fw-semibold mb-2">Assignment Period</div>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <Form.Control
                type="date"
                aria-label="Start date"
                style={{ width: 150 }}
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
              <Form.Control
                type="time"
                aria-label="Start time"
                aria-invalid={invalid === NO_START_TIME ? true : undefined}
                aria-describedby={invalid === NO_START_TIME ? windowErrorId : undefined}
                style={{ width: 110 }}
                value={startTime}
                onChange={(event) => setStartTime(event.target.value)}
              />
              <span className="text-muted">~</span>
              <Form.Control
                type="date"
                aria-label="End date"
                aria-invalid={missingEndDate ? true : undefined}
                aria-describedby={missingEndDate ? windowErrorId : undefined}
                style={{ width: 150 }}
                disabled={forever}
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
              <Form.Control
                type="time"
                aria-label="End time"
                aria-invalid={missingEndTime ? true : undefined}
                aria-describedby={missingEndTime ? windowErrorId : undefined}
                style={{ width: 110 }}
                disabled={forever}
                value={endTime}
                onChange={(event) => setEndTime(event.target.value)}
              />
            </div>
            {invalid && (
              <div id={windowErrorId} className="text-danger small mt-2" role="alert">
                {invalid}
              </div>
            )}
            {scheduled && (
              <div className="mt-2">
                <span className="badge bg-secondary">SCHEDULED</span>{' '}
                <span className="text-muted small">This window opens later, so it is not assigning yet.</span>
              </div>
            )}
            <Form.Check
              type="checkbox"
              id="sample-forever"
              className="mt-2"
              label="forever (no end date)"
              checked={forever}
              onChange={(event) => {
                setForever(event.target.checked);
                // Ticking forever means no end: a hidden end left behind would come back on the next untick.
                if (event.target.checked) {
                  setEndDate('');
                  setEndTime('');
                }
              }}
            />
          </>
        )}

        {/* The dual view is deliberate: the carrier sees a suffix, the internal paperwork sees the set. */}
        <Alert variant="info" className="mt-3 mb-0 small">
          No sample product type is chosen here. While a window is open, <b>&quot;(+ sample set)&quot;</b> is appended
          to the last product name of a target order and a sample-set row is added to the invoice. Periods may overlap,
          but an order still receives exactly one sample set.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={close}>
          Cancel
        </Button>
        <Button variant="success" disabled={busy || invalid !== null} onClick={() => void start()}>
          Start Assignment (ON)
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
